/**
 * Record updates: metadata housekeeping — titles, owners, review cadence,
 * departments, jurisdictions, shelves, frameworks, control links, the company
 * profile. Not gaps: no severity, not counted with findings.
 *
 * Linear-style compact rows grouped by document: field, then before → after
 * as chips (full value on hover); the reason on hover; Apply / Skip as icon
 * buttons on hover (always shown on touch); Undo on a decided row. Three or
 * more control links on one document fold into a bundle. Keys on a focused
 * row: a apply · s skip · u undo · j/k move · f or / filter.
 *
 * @module components/regulation/recordUpdates/RecordUpdatesPage
 */
import { Plot } from "@lucasmarkes/hairline/react";
import { Link } from "@tanstack/react-router";
import {
  CheckIcon,
  ChevronRightIcon,
  CircleCheckIcon,
  CircleDashedIcon,
  CircleSlashIcon,
  ClipboardListIcon,
  FileTextIcon,
  InfoIcon,
  KeyboardIcon,
  ListFilterIcon,
  RotateCcwIcon,
  SearchIcon,
  TriangleAlertIcon,
  XIcon,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";

import { HairlineArt } from "~/components/ui/hairline-art";
import { cn } from "~/lib/utils";
import { useRecordUpdates, type RecordUpdatesView } from "../../../state/recordUpdates";
import { Badge } from "../../ui/badge";
import { Button } from "../../ui/button";
import { Checkbox } from "../../ui/checkbox";
import { Kbd } from "../../ui/kbd";
import { Popover, PopoverPopup, PopoverTrigger } from "../../ui/popover";
import { toastManager } from "../../ui/toast";
import { Tooltip, TooltipPopup, TooltipTrigger } from "../../ui/tooltip";
import { isTyping } from "../finding/FindingsQueue";
import { PageHeader, SegmentedTabs } from "../PageHeader";
import {
  EMPTY_RECORD_FILTER,
  RECORD_LABELS,
  activeRecordFilterCount,
  addedWords,
  bundleRecordUpdates,
  canUndoRecord,
  filterRecordUpdates,
  groupRecordUpdates,
  matchesRecordTab,
  recordCounts,
  summariseOutcomes,
  type RecordAction,
  type RecordFilter,
  type RecordOutcome,
  type RecordTab,
  type RecordUpdate,
} from "./recordUpdates.logic";

const CHIP = "inline-block h-5 min-w-0 max-w-full shrink truncate rounded-[5px] px-1.5 align-middle text-xs leading-5 sm:max-w-60";
const BEFORE = cn(CHIP, "bg-muted text-muted-foreground");
const AFTER = cn(CHIP, "bg-emerald-500/10 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-200");
const REMOVED = cn(CHIP, "bg-red-500/10 text-red-700 line-through decoration-red-500/50 dark:text-red-300");

type Run = (action: RecordAction, updates: ReadonlyArray<RecordUpdate>) => Promise<void>;

interface RowContext {
  readonly canManage: boolean;
  readonly run: Run;
  readonly busy: ReadonlySet<string>;
}

/** A value chip; the full value on hover, since chips truncate. */
function ValueChip({ className, children, full }: { className: string; children: ReactNode; full: string }) {
  return (
    <Tooltip>
      <TooltipTrigger render={<span className={className} tabIndex={-1} />}>{children}</TooltipTrigger>
      <TooltipPopup className="max-w-80 whitespace-pre-wrap break-words">{full}</TooltipPopup>
    </Tooltip>
  );
}

function ValueChange({ update, expanded }: { update: RecordUpdate; expanded: boolean }) {
  if (update.kind === "org_context") {
    if (expanded) return null;
    return (
      <ValueChip className={AFTER} full={update.addition ?? ""}>
        +{addedWords(update.addition)} words
      </ValueChip>
    );
  }
  if (update.op === "add")
    return (
      <ValueChip className={AFTER} full={update.after}>
        + {update.after}
      </ValueChip>
    );
  if (update.op === "remove")
    return (
      <ValueChip className={REMOVED} full={update.after}>
        {update.after}
      </ValueChip>
    );
  return (
    <span className="flex min-w-0 items-center gap-1.5 max-sm:flex-1">
      {update.before === null ? (
        <span className={cn(BEFORE, "italic")} title="Applied before earlier values were kept">
          Earlier value not kept
        </span>
      ) : (
        <ValueChip className={cn(BEFORE, "shrink-0 max-sm:max-w-[45%] sm:max-w-60")} full={update.before}>
          {update.before}
        </ValueChip>
      )}
      <span aria-hidden className="text-muted-foreground/60">
        →
      </span>
      <span className="sr-only">changes to</span>
      <ValueChip className={AFTER} full={update.after}>
        {update.after}
      </ValueChip>
    </span>
  );
}

function IconAction({
  label,
  shortcut,
  onClick,
  disabled,
  children,
}: {
  label: string;
  shortcut: string;
  onClick: () => void;
  disabled?: boolean;
  children: ReactNode;
}) {
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <button
            type="button"
            aria-label={label}
            disabled={disabled}
            onClick={onClick}
            data-record-action={label.split(" ")[0]?.toLowerCase()}
            className="flex size-6 cursor-pointer items-center justify-center rounded-md text-muted-foreground transition-colors duration-150 hover:bg-foreground/[0.08] hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-default disabled:opacity-40"
          />
        }
      >
        {children}
      </TooltipTrigger>
      <TooltipPopup>
        {label} <Kbd className="ms-1">{shortcut}</Kbd>
      </TooltipPopup>
    </Tooltip>
  );
}

const STATUS_LABEL = { pending: "Pending", applied: "Applied", skipped: "Skipped" } as const;

function RecordUpdateRow({ update, context }: { update: RecordUpdate; context: RowContext }) {
  const { canManage, run, busy } = context;
  const [expanded, setExpanded] = useState(false);
  const pending = busy.has(update.id);
  const StatusIcon = update.status === "applied" ? CircleCheckIcon : update.status === "skipped" ? CircleSlashIcon : CircleDashedIcon;
  return (
    <li
      data-record-update={update.id}
      data-record-row=""
      data-status={update.status}
      tabIndex={0}
      onKeyDown={(event) => {
        if (event.target !== event.currentTarget || !canManage || pending || event.metaKey || event.ctrlKey) return;
        if (event.key === "a" && update.status === "pending") void run("apply", [update]);
        else if (event.key === "s" && update.status === "pending") void run("skip", [update]);
        else if (event.key === "u" && canUndoRecord(update)) void run("undo", [update]);
        else return;
        event.preventDefault();
      }}
      className="group/record outline-none transition-colors duration-150 hover:bg-foreground/[0.03] focus-visible:bg-foreground/[0.05] focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-ring/50"
    >
      <div className="flex min-h-10 flex-wrap items-center gap-x-3 gap-y-1 py-2 pe-4 ps-4 text-[13px] sm:flex-nowrap sm:py-0 sm:pe-6 sm:ps-6">
        <StatusIcon
          aria-label={STATUS_LABEL[update.status]}
          className={cn("size-3.5 shrink-0", update.status === "applied" ? "text-emerald-600 dark:text-emerald-400" : "text-muted-foreground/70")}
        />
        <span className="min-w-0 truncate text-foreground/85 max-sm:flex-1 sm:w-36 sm:shrink-0">{update.label}</span>
        <span
          className={cn(
            "order-last flex min-w-0 flex-1 items-center gap-2 max-sm:basis-full max-sm:ps-6.5 sm:order-none",
            update.status === "skipped" && "opacity-50",
          )}
        >
          <ValueChange update={update} expanded={expanded} />
          {update.kind === "org_context" ? (
            <button
              type="button"
              aria-expanded={expanded}
              onClick={() => setExpanded((value) => !value)}
              className="flex shrink-0 cursor-pointer items-center gap-0.5 text-xs text-muted-foreground hover:text-foreground"
            >
              <ChevronRightIcon aria-hidden className={cn("size-3 transition-transform duration-150", expanded && "rotate-90")} />
              {expanded ? "Hide text" : "Show text"}
            </button>
          ) : null}
          <Tooltip>
            <TooltipTrigger
              render={
                <button
                  type="button"
                  aria-label={`Why: ${update.why}`}
                  className="shrink-0 cursor-help rounded-sm text-muted-foreground/50 opacity-0 outline-none transition-opacity duration-150 focus-visible:opacity-100 focus-visible:ring-2 focus-visible:ring-ring group-focus-within/record:opacity-100 group-hover/record:opacity-100 pointer-coarse:opacity-100"
                />
              }
            >
              <InfoIcon aria-hidden className="size-3.5" />
            </TooltipTrigger>
            <TooltipPopup className="max-w-80">{update.why}</TooltipPopup>
          </Tooltip>
        </span>
        <span className="flex h-6 shrink-0 items-center gap-0.5">
          {update.status === "pending" ? (
            canManage ? (
              <span className="flex items-center gap-0.5 opacity-0 transition-opacity duration-150 group-focus-within/record:opacity-100 group-hover/record:opacity-100 pointer-coarse:opacity-100">
                <IconAction label="Apply" shortcut="A" disabled={pending} onClick={() => void run("apply", [update])}>
                  <CheckIcon className="size-3.5" />
                </IconAction>
                <IconAction label="Skip" shortcut="S" disabled={pending} onClick={() => void run("skip", [update])}>
                  <XIcon className="size-3.5" />
                </IconAction>
              </span>
            ) : (
              <span className="text-xs text-muted-foreground">Waiting for an admin</span>
            )
          ) : update.legacy ? (
            <span className="text-xs text-muted-foreground" title="Applied before earlier values were kept">
              Applied · no undo
            </span>
          ) : (
            <>
              <span className="text-xs text-muted-foreground">{STATUS_LABEL[update.status]}</span>
              {canManage ? (
                <IconAction label="Undo" shortcut="U" disabled={pending} onClick={() => void run("undo", [update])}>
                  <RotateCcwIcon className="size-3.5" />
                </IconAction>
              ) : null}
            </>
          )}
        </span>
      </div>
      {update.kind === "org_context" && expanded && update.addition ? (
        <div className="px-4 pb-3 sm:ps-[3.65rem] sm:pe-6">
          <p className="rounded-md border border-border/60 bg-muted/20 px-3 py-2 text-[13px] leading-6 whitespace-pre-wrap">{update.addition}</p>
        </div>
      ) : null}
    </li>
  );
}

/** Several control links as one row of chips; opens to the single rows. a / s apply or skip every pending one. */
function RecordUpdateBundle({ label, updates, context }: { label: string; updates: ReadonlyArray<RecordUpdate>; context: RowContext }) {
  const { canManage, run, busy } = context;
  const [open, setOpen] = useState(false);
  const pending = updates.filter((update) => update.status === "pending");
  const skippedAll = updates.every((update) => update.status === "skipped");
  const working = updates.some((update) => busy.has(update.id));
  const StatusIcon = pending.length ? CircleDashedIcon : skippedAll ? CircleSlashIcon : CircleCheckIcon;
  return (
    <li
      data-record-bundle={label}
      data-record-row=""
      tabIndex={0}
      onKeyDown={(event) => {
        if (event.target !== event.currentTarget || !canManage || working || event.metaKey || event.ctrlKey || pending.length === 0) return;
        if (event.key === "a") void run("apply", pending);
        else if (event.key === "s") void run("skip", pending);
        else return;
        event.preventDefault();
      }}
      className="group/record outline-none transition-colors duration-150 hover:bg-foreground/[0.03] focus-visible:bg-foreground/[0.05] focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-ring/50"
    >
      <div className="flex min-h-10 flex-wrap items-center gap-x-3 gap-y-1.5 py-2 pe-4 ps-4 text-[13px] sm:flex-nowrap sm:pe-6 sm:ps-6">
        <StatusIcon
          aria-label={pending.length ? `${pending.length} pending` : skippedAll ? "Skipped" : "Applied"}
          className={cn("size-3.5 shrink-0", !pending.length && !skippedAll ? "text-emerald-600 dark:text-emerald-400" : "text-muted-foreground/70")}
        />
        <button
          type="button"
          aria-expanded={open}
          onClick={() => setOpen((value) => !value)}
          className="flex min-w-0 cursor-pointer items-center gap-1 text-left text-foreground/85 outline-none hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring max-sm:flex-1 sm:w-36 sm:shrink-0"
        >
          <span className="truncate">{label}</span>
          <span className="shrink-0 text-xs tabular-nums text-muted-foreground">{updates.length}</span>
          <ChevronRightIcon aria-hidden className={cn("size-3 shrink-0 text-muted-foreground transition-transform duration-150", open && "rotate-90")} />
        </button>
        <span className="order-last flex min-w-0 flex-1 flex-wrap gap-1 max-sm:basis-full max-sm:ps-6.5 sm:order-none">
          {updates.map((update) => (
            <ValueChip
              key={update.id}
              full={`${update.after}: ${update.why}`}
              className={cn(
                update.op === "remove" ? REMOVED : AFTER,
                "max-w-56",
                update.status === "skipped" && "line-through opacity-50",
                update.status === "applied" && "opacity-70",
              )}
            >
              {update.op === "add" ? "+ " : ""}
              {update.after}
            </ValueChip>
          ))}
        </span>
        <span className="flex h-6 shrink-0 items-center gap-0.5">
          {pending.length === 0 ? (
            <span className="text-xs text-muted-foreground">{skippedAll ? "Skipped" : "Applied"}</span>
          ) : canManage ? (
            <span className="flex items-center gap-0.5 opacity-0 transition-opacity duration-150 group-focus-within/record:opacity-100 group-hover/record:opacity-100 pointer-coarse:opacity-100">
              <IconAction label={`Apply all ${pending.length}`} shortcut="A" disabled={working} onClick={() => void run("apply", pending)}>
                <CheckIcon className="size-3.5" />
              </IconAction>
              <IconAction label={`Skip all ${pending.length}`} shortcut="S" disabled={working} onClick={() => void run("skip", pending)}>
                <XIcon className="size-3.5" />
              </IconAction>
            </span>
          ) : (
            <span className="text-xs text-muted-foreground">Waiting for an admin</span>
          )}
        </span>
      </div>
      {open ? (
        <ul className="divide-y divide-border/40 border-t border-border/40 bg-muted/20 sm:ps-6">
          {updates.map((update) => (
            <RecordUpdateRow key={update.id} update={update} context={context} />
          ))}
        </ul>
      ) : null}
    </li>
  );
}

function RecordItems({ updates, context }: { updates: ReadonlyArray<RecordUpdate>; context: RowContext }) {
  return (
    <>
      {bundleRecordUpdates(updates).map((item) =>
        item.kind === "one" ? (
          <RecordUpdateRow key={item.update.id} update={item.update} context={context} />
        ) : (
          <RecordUpdateBundle key={item.key} label={item.label} updates={item.updates} context={context} />
        ),
      )}
    </>
  );
}

/** What a batch did, row by row, as a card under the header. */
function BatchResults({ action, outcomes, onClose }: { action: RecordAction; outcomes: ReadonlyArray<RecordOutcome>; onClose: () => void }) {
  return (
    <div role="status" className="border-b border-border/60 bg-muted/20 px-4 py-2.5 sm:px-6" data-record-results="">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-medium">{summariseOutcomes(action, outcomes)}</p>
        <Button size="icon-xs" variant="ghost" aria-label="Close results" onClick={onClose}>
          <XIcon />
        </Button>
      </div>
      <ul className="mt-1 max-h-40 space-y-0.5 overflow-y-auto">
        {outcomes.map((outcome) => (
          <li key={outcome.id} className="flex min-w-0 items-center gap-2 text-xs leading-5">
            {outcome.error === null ? (
              <CircleCheckIcon aria-label="Done" className="size-3.5 shrink-0 text-emerald-600 dark:text-emerald-400" />
            ) : (
              <TriangleAlertIcon aria-label="Failed" className="size-3.5 shrink-0 text-amber-600 dark:text-amber-300" />
            )}
            <span className="shrink-0 font-medium">{outcome.label}</span>
            <span className="min-w-0 truncate text-muted-foreground" title={outcome.error ?? outcome.targetName}>
              {outcome.error ?? outcome.targetName}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** j/k between focusable rows inside `root`. */
function useRecordKeys(root: React.RefObject<HTMLElement | null>) {
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.altKey) return;
      if (event.key !== "j" && event.key !== "k") return;
      if (isTyping(event.target) && !(event.target as HTMLElement).hasAttribute("data-record-row")) return;
      const rows = [...(root.current?.querySelectorAll<HTMLElement>("[data-record-row]") ?? [])];
      if (rows.length === 0) return;
      event.preventDefault();
      const index = rows.indexOf(document.activeElement as HTMLElement);
      const next = index === -1 ? 0 : Math.min(rows.length - 1, Math.max(0, index + (event.key === "j" ? 1 : -1)));
      rows[next]?.focus();
      rows[next]?.scrollIntoView({ block: "nearest" });
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [root]);
}

const toggle = <T,>(list: ReadonlyArray<T>, value: T, on: boolean) => (on ? [...list, value] : list.filter((entry) => entry !== value));

function FilterPopover({
  open,
  onOpenChange,
  filter,
  setFilter,
  updates,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  filter: RecordFilter;
  setFilter: (next: RecordFilter) => void;
  /** Rows of the current tab, for the counts beside each field. */
  updates: ReadonlyArray<RecordUpdate>;
}) {
  const searchRef = useRef<HTMLInputElement>(null);
  const active = activeRecordFilterCount(filter);
  const kinds = [...new Set(updates.map((update) => update.kind))].sort((left, right) =>
    (RECORD_LABELS[left] ?? left).localeCompare(RECORD_LABELS[right] ?? right),
  );
  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <Tooltip>
        <TooltipTrigger
          render={
            <PopoverTrigger render={<Button variant="ghost" size="xs" aria-label={`Filter${active ? `, ${active} active` : ""}`} data-record-filter="" />}>
              <ListFilterIcon aria-hidden />
              <span className="max-sm:hidden">Filter</span>
              {active > 0 ? (
                <Badge variant="secondary" size="sm" className="tabular-nums">
                  {active}
                </Badge>
              ) : null}
            </PopoverTrigger>
          }
        />
        <TooltipPopup>
          Filter and search <Kbd className="ms-1">F</Kbd> <Kbd>/</Kbd>
        </TooltipPopup>
      </Tooltip>
      <PopoverPopup align="end" className="w-72" viewportClassName="p-0 [--viewport-inline-padding:0px]" initialFocus={searchRef}>
        <label className="flex h-10 items-center gap-2 border-b border-border/70 px-3">
          <SearchIcon aria-hidden className="size-3.5 shrink-0 text-muted-foreground" />
          <input
            ref={searchRef}
            value={filter.query}
            onChange={(event) => setFilter({ ...filter, query: event.currentTarget.value })}
            placeholder="Search suggestions…"
            aria-label="Search suggestions"
            className="min-w-0 flex-1 bg-transparent text-[13px] outline-none placeholder:text-muted-foreground/70"
          />
          {filter.query ? (
            <button type="button" aria-label="Clear search" onClick={() => setFilter({ ...filter, query: "" })} className="cursor-pointer rounded-sm text-muted-foreground hover:text-foreground">
              <XIcon className="size-3.5" />
            </button>
          ) : null}
        </label>
        <fieldset className="max-h-[min(24rem,60dvh)] min-w-0 overflow-y-auto p-1.5">
          <legend className="px-2 pb-1 pt-1.5 text-[11px] font-medium text-muted-foreground">Field</legend>
          {kinds.map((kind) => (
            <label key={kind} className="flex h-7 cursor-pointer items-center gap-2 rounded-md px-2 text-[13px] transition-colors duration-100 hover:bg-foreground/[0.06]">
              <Checkbox
                className="size-3.5 sm:size-3.5"
                checked={filter.kinds.includes(kind)}
                onCheckedChange={(checked) => setFilter({ ...filter, kinds: toggle(filter.kinds, kind, checked === true) })}
              />
              <span className="min-w-0 flex-1 truncate">{RECORD_LABELS[kind] ?? kind}</span>
              <span className="text-[11px] tabular-nums text-muted-foreground">{updates.filter((update) => update.kind === kind).length}</span>
            </label>
          ))}
        </fieldset>
        {active > 0 ? (
          <div className="border-t border-border/70 p-1.5">
            <Button variant="ghost" size="xs" className="w-full justify-start" onClick={() => setFilter(EMPTY_RECORD_FILTER)}>
              <XIcon aria-hidden />
              Clear filters
            </Button>
          </div>
        ) : null}
      </PopoverPopup>
    </Popover>
  );
}

const SHORTCUTS: ReadonlyArray<readonly [string, string]> = [
  ["J K", "Move"],
  ["A", "Apply"],
  ["S", "Skip"],
  ["U", "Undo"],
  ["F", "Filter"],
];

const TOAST_TITLE: Record<RecordAction, string> = { apply: "Applied", skip: "Skipped", undo: "Undone" };

/**
 * Runs row actions: marks the rows busy, then a toast for one row or a
 * results card for a batch, with the API's message on a refusal.
 */
function useRecordRun(view: Pick<RecordUpdatesView, "run">) {
  const [busy, setBusy] = useState<ReadonlySet<string>>(new Set());
  const [results, setResults] = useState<{ action: RecordAction; outcomes: ReadonlyArray<RecordOutcome> } | null>(null);
  // `busy` is state and lags a double trigger; this ref drops rows already in flight.
  const inFlight = useRef(new Set<string>());
  const run: Run = async (action, rows) => {
    const fresh = rows.filter((row) => !inFlight.current.has(row.id));
    if (fresh.length === 0) return;
    const ids = fresh.map((row) => row.id);
    for (const id of ids) inFlight.current.add(id);
    setBusy((current) => new Set([...current, ...ids]));
    try {
      const outcomes = await view.run(action, fresh);
      const failed = outcomes.filter((outcome) => outcome.error !== null);
      if (outcomes.length > 1) setResults({ action, outcomes });
      else if (failed[0]) toastManager.add({ type: "error", title: `Could not ${action}`, description: failed[0].error ?? undefined });
      else if (outcomes[0]) toastManager.add({ type: "success", title: TOAST_TITLE[action], description: `${outcomes[0].label} · ${outcomes[0].targetName}` });
    } finally {
      for (const id of ids) inFlight.current.delete(id);
      setBusy((current) => new Set([...current].filter((id) => !ids.includes(id))));
    }
  };
  return { run, busy, results, clearResults: () => setResults(null) };
}

export function RecordUpdatesPage({ orgSegment, view }: { orgSegment: string; view: RecordUpdatesView }) {
  const { updates, canManage } = view;
  const [tab, setTab] = useState<RecordTab>("pending");
  const [filter, setFilter] = useState<RecordFilter>(EMPTY_RECORD_FILTER);
  const [filterOpen, setFilterOpen] = useState(false);
  const { run, busy, results, clearResults } = useRecordRun(view);
  // Rows decided on the Pending tab stay in place (with Undo) until the tab changes.
  const pendingIds = () => new Set(updates.filter((update) => update.status === "pending").map((update) => update.id));
  const [kept, setKept] = useState<ReadonlySet<string>>(pendingIds);
  const seeded = useRef(updates.length > 0);
  useEffect(() => {
    if (seeded.current || updates.length === 0) return;
    seeded.current = true;
    setKept(pendingIds());
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- seed once, when the rows first arrive
  }, [updates]);

  const context: RowContext = { canManage, run, busy };

  const counts = useMemo(() => recordCounts(filterRecordUpdates(updates, filter)), [updates, filter]);
  const inTab = useMemo(
    () => updates.filter((update) => (tab === "pending" ? update.status === "pending" || kept.has(update.id) : matchesRecordTab(update, tab))),
    [updates, tab, kept],
  );
  const shown = useMemo(() => filterRecordUpdates(inTab, filter), [inTab, filter]);
  const groups = useMemo(() => groupRecordUpdates(shown), [shown]);
  const pending = shown.filter((update) => update.status === "pending");
  const filtered = activeRecordFilterCount(filter) > 0;

  const pageRef = useRef<HTMLDivElement>(null);
  useRecordKeys(pageRef);
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.altKey || isTyping(event.target)) return;
      if (event.key === "/" || event.key === "f") {
        event.preventDefault();
        setFilterOpen(true);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  return (
    <div ref={pageRef} data-regulation-page="true" data-record-updates="" className="relative flex min-h-0 flex-1 flex-col">
      <PageHeader
        title="Suggestions"
        tabs={
          <SegmentedTabs
            label="Suggestion status"
            value={tab}
            onValueChange={(next) => {
              setTab(next);
              setKept(pendingIds());
            }}
            tabs={[
              { value: "pending", label: "Pending", count: counts.pending },
              { value: "applied", label: "Applied", count: counts.applied },
              { value: "skipped", label: "Skipped", count: counts.skipped },
              { value: "all", label: "All", count: counts.all },
            ]}
          />
        }
        actions={
          <>
            {filter.query.trim() ? (
              <Button size="xs" variant="secondary" className="max-w-40" onClick={() => setFilterOpen(true)} aria-label={`Search: ${filter.query}`}>
                <SearchIcon aria-hidden />
                <span className="truncate">{filter.query}</span>
              </Button>
            ) : null}
            <FilterPopover open={filterOpen} onOpenChange={setFilterOpen} filter={filter} setFilter={setFilter} updates={inTab} />
            <Tooltip>
              <TooltipTrigger render={<Button size="icon-xs" variant="ghost" aria-label="Keyboard shortcuts" className="max-sm:hidden" />}>
                <KeyboardIcon />
              </TooltipTrigger>
              <TooltipPopup>
                <span className="grid grid-cols-[auto_auto] items-center gap-x-3 gap-y-1">
                  {SHORTCUTS.map(([keys, label]) => (
                    <span key={label} className="contents">
                      <span className="flex gap-0.5">
                        {keys.split(" ").map((key) => (
                          <Kbd key={key}>{key}</Kbd>
                        ))}
                      </span>
                      <span>{label}</span>
                    </span>
                  ))}
                </span>
              </TooltipPopup>
            </Tooltip>
            {canManage && pending.length > 1 ? (
              <Button size="xs" variant="outline" disabled={pending.some((update) => busy.has(update.id))} onClick={() => void run("apply", pending)} data-apply-all-records="">
                <CheckIcon aria-hidden />
                Apply all
                <span className="tabular-nums text-muted-foreground">{pending.length}</span>
              </Button>
            ) : null}
          </>
        }
      />
      {results ? <BatchResults action={results.action} outcomes={results.outcomes} onClose={clearResults} /> : null}
      <div className="min-h-0 flex-1 overflow-y-auto pb-16" aria-label="Suggestions">
        {groups.length === 0 ? (
          <div className="flex flex-col items-center gap-3 px-6 py-20 text-center text-[13px] text-muted-foreground">
            {filtered ? (
              <CircleCheckIcon aria-hidden className="size-6 text-muted-foreground/50" />
            ) : (
              <HairlineArt figure={Plot} className="mb-2 w-48 sm:w-56" />
            )}
            {filtered ? "No suggestions match" : tab === "pending" ? "Nothing to update. The records match the documents." : "Nothing here"}
            {filtered ? (
              <Button size="xs" variant="outline" onClick={() => setFilter(EMPTY_RECORD_FILTER)}>
                Clear filters
              </Button>
            ) : null}
          </div>
        ) : (
          groups.map((group) => {
            const groupPending = group.updates.filter((update) => update.status === "pending");
            return (
              <section key={group.key} aria-label={group.title} data-record-group={group.key}>
                <div className="group/header sticky top-0 z-10 flex h-9 items-center gap-2 border-b border-border/60 bg-muted/60 pe-4 ps-4 text-[13px] backdrop-blur sm:pe-6 sm:ps-6 dark:bg-muted/40">
                  {group.policyId === null ? (
                    <ClipboardListIcon aria-hidden className="size-3.5 shrink-0 text-muted-foreground" />
                  ) : (
                    <FileTextIcon aria-hidden className="size-3.5 shrink-0 text-muted-foreground" />
                  )}
                  {group.policyId ? (
                    <Link
                      to="/$org/compliance/policies/$policyId"
                      params={{ org: orgSegment, policyId: group.policyId }}
                      className="min-w-0 truncate font-medium hover:underline"
                      title={group.title}
                    >
                      {group.title}
                    </Link>
                  ) : (
                    <span className="min-w-0 truncate font-medium">{group.title}</span>
                  )}
                  <span className="shrink-0 text-xs tabular-nums text-muted-foreground">{group.updates.length}</span>
                  {canManage && groupPending.length > 1 ? (
                    <Button
                      size="xs"
                      variant="ghost"
                      className="ms-auto h-6 shrink-0 opacity-0 transition-opacity duration-150 focus-visible:opacity-100 group-hover/header:opacity-100 pointer-coarse:opacity-100"
                      disabled={groupPending.some((update) => busy.has(update.id))}
                      onClick={() => void run("apply", groupPending)}
                      data-apply-group=""
                    >
                      <CheckIcon aria-hidden />
                      Apply {groupPending.length}
                    </Button>
                  ) : null}
                </div>
                <ul className="divide-y divide-border/50">
                  <RecordItems updates={group.updates} context={context} />
                </ul>
              </section>
            );
          })
        )}
      </div>
    </div>
  );
}

/**
 * One document's record updates, for its policy page tab: the same rows,
 * Apply / Skip / Undo, and Apply all for the document. Renders nothing
 * while loading or when the document has no record updates.
 */
export function RecordUpdatesStrip({ organizationId, policyId }: { organizationId: string | null; policyId: string }) {
  const view = useRecordUpdates(organizationId);
  const { run, busy, results, clearResults } = useRecordRun(view);
  const updates = useMemo(() => view.updates.filter((update) => update.target === policyId), [view.updates, policyId]);
  const ref = useRef<HTMLElement>(null);
  useRecordKeys(ref);
  if (!view.ready || updates.length === 0) return null;
  const pending = updates.filter((update) => update.status === "pending");
  const context: RowContext = { canManage: view.canManage, run, busy };
  return (
    <section ref={ref} aria-label="Suggestions" data-record-strip={policyId} className="overflow-hidden rounded-lg border border-border/60 bg-card">
      <div className="flex h-9 items-center gap-2 border-b border-border/60 bg-muted/40 px-4 text-[13px]">
        <ClipboardListIcon aria-hidden className="size-3.5 text-muted-foreground" />
        <span className="font-medium">Suggestions</span>
        <span className="text-xs tabular-nums text-muted-foreground">{pending.length ? `${pending.length} pending` : "Done"}</span>
        {view.canManage && pending.length > 1 ? (
          <Button size="xs" variant="ghost" className="ms-auto h-6" disabled={pending.some((update) => busy.has(update.id))} onClick={() => void run("apply", pending)}>
            <CheckIcon aria-hidden />
            Apply all {pending.length}
          </Button>
        ) : null}
      </div>
      {results ? <BatchResults action={results.action} outcomes={results.outcomes} onClose={clearResults} /> : null}
      <ul className="divide-y divide-border/50">
        <RecordItems updates={updates} context={context} />
      </ul>
    </section>
  );
}
